import { defineStore } from 'pinia';

export const useProductsStore = defineStore('productsStore', {
  state: () => {
    return {
      products: [],
      filters: [],
      productsFiltered: [],
      system: 'metric',
      displayToggle: false,
      locales: '',
      openedDropdown: null,
      dynamicFiltersArr: [],
      hideFilterMobile: false,
    };
  },
  getters: {
    isSystemMetric: (state) => state.system === 'metric',
    selectedFilters: (state) => state.dynamicFiltersArr.filter((filter) => filter.selected),
    filteredProductsCount: (state) => state.productsFiltered.length,
    allProductsCount: (state) => state.products.length,
    selectedFiltersListString: (state) => {
      let selectedFiltersString = '';
      state.selectedFilters.forEach((filter, i) => {
        i > 0 && (selectedFiltersString += ' | ');
        selectedFiltersString += filter.name;
        selectedFiltersString += ' : ';
        selectedFiltersString += state.selectedOption(state.options(filter)).label;
      });
      return selectedFiltersString;
    },
  },
  actions: {
    sortProducts(products) {
      this.products = [
        ...products.filter(
          (item) =>
            item.productData.find((prop) => prop.type === 'new' && prop.value === 'true') &&
            item.productData.find((prop) => prop.type === 'popular' && prop.value === 'true'),
        ),
        ...products.filter(
          (item) =>
            item.productData.find((prop) => prop.type === 'new' && prop.value === 'true') &&
            !item.productData.find((prop) => prop.type === 'popular' && prop.value === 'true'),
        ),
        ...products.filter(
          (item) =>
            !item.productData.find((prop) => prop.type === 'new' && prop.value === 'true') &&
            item.productData.find((prop) => prop.type === 'popular' && prop.value === 'true'),
        ),
        ...products.filter(
          (item) =>
            !item.productData.find((prop) => prop.type === 'new' && prop.value === 'true') &&
            !item.productData.find((prop) => prop.type === 'popular' && prop.value === 'true'),
        ),
      ];
      this.productsFiltered = this.products;
    },
    setLocales() {
      this.locales =
        navigator.userLanguage ||
        (navigator.languages && navigator.languages.length && navigator.languages[0]) ||
        navigator.language ||
        navigator.browserLanguage ||
        navigator.systemLanguage ||
        'en';
    },
    resetDynamicFiltersArr() {
      // deep copy
      this.dynamicFiltersArr = JSON.parse(JSON.stringify(this.filters));
      this.openedDropdown = null;
      this.filterEverything();
    },
    removeOneFilter(filterId) {
      let arr = [];
      this.dynamicFiltersArr = this.dynamicFiltersArr.map((filter, i) => {
        if (filter.id !== filterId) {
          arr.push(JSON.parse(JSON.stringify(filter)));
        } else {
          arr.push(JSON.parse(JSON.stringify(this.filters[i])));
        }
      });
      this.dynamicFiltersArr = arr;
      this.openedDropdown = null;
      this.filterEverything();
    },
    options(filter) {
      return filter[filter.options ? 'options' : `${this.system}Options`];
    },
    value(data) {
      return (
        data[`${this.system}Value`] ||
        data[`${this.system}ValueRange`] ||
        data[`valueRange`] ||
        data.value ||
        ''
      );
    },
    selectedOption(options) {
      return options.filter((option) => option.selected)[0];
    },
    isRangeFilterMatching(productValue, optionValue) {
      let firstProductVal = productValue[0];
      let firstOptionVal = optionValue[0];

      // no value for the second element or range array = Infinity
      let lastProductVal = productValue[1] ? productValue[1] : Infinity;
      let lastOptionVal = optionValue[1] ? optionValue[1] : Infinity;

      if (Array.isArray(productValue)) {
        return !(lastProductVal < firstOptionVal || firstProductVal > lastOptionVal);
      } else {
        return productValue >= firstOptionVal && productValue <= lastOptionVal;
      }
    },
    isNumberFilterMatchingWithRange(selectedValue, productValue) {
      return selectedValue >= productValue[0] && selectedValue <= productValue[1];
    },
    filterEverything() {
      this.productsFiltered = this.products.filter((product) => {
        let resultsArr = [];
        let filterFound = 0;
        product.productData.forEach((data) => {
          this.selectedFilters.forEach((filter) => {
            const selectedNonRangeValue = this.selectedOption(this.options(filter)).value;
            const selectedRangeValue = this.selectedOption(this.options(filter)).valueRange;

            const productValue = this.value(data);
            let isMatching;
            if (data.propertyId === filter.id) {
              if (filter.type === 'range') {
                isMatching = this.isRangeFilterMatching(productValue, selectedRangeValue);
              } else if (filter.type === 'number' && Array.isArray(productValue)) {
                isMatching = isNumberFilterMatchingWithRange(selectedNonRangeValue, productValue);
              } else {
                isMatching = selectedNonRangeValue === productValue;
              }
              resultsArr.push(isMatching);
              filterFound++;
            }
          });
        });
        const allChecksSuccessful = resultsArr.every((value) => value === true);
        return allChecksSuccessful && filterFound === this.selectedFilters.length;
      });
      this.disableNotMatchingOptions();
    },
    disableNotMatchingOptions() {
      this.dynamicFiltersArr.forEach((filter) => {
        this.options(filter).forEach((option) => {
          let optionAvailable = [];
          this.productsFiltered.forEach((product) => {
            product.productData.forEach((data) => {
              const optionNonRangeValue = option.value;
              const optionRangeValue = option.valueRange;

              const productValue = this.value(data);
              if (data.propertyId === filter.id) {
                let isMatching;
                if (filter.type === 'range') {
                  isMatching = this.isRangeFilterMatching(productValue, optionRangeValue);
                } else if (filter.type === 'number' && Array.isArray(productValue)) {
                  isMatching =
                    optionNonRangeValue >= productValue[0] &&
                    optionNonRangeValue <= productValue[1];
                } else {
                  isMatching = optionNonRangeValue === productValue;
                }
                optionAvailable.push(isMatching);
              }
            });
          });

          // disable option that would return no products
          const atLeastOnePossible = optionAvailable.some((value) => value === true);
          option.disabled = !atLeastOnePossible;

          // hide the whole filter if it's boolean with only one disabled option
          if (filter.type === 'boolean') {
            const allValuesTheSame = optionAvailable.every((value) => value === optionAvailable[0]);
            filter.hidden = allValuesTheSame;
          }

          this.openedDropdown = null;
        });
      });
    },
    manageSystemToggleDisplay(productData) {
      let keys = [];

      // for multiple products check
      if (this.products.length) {
        this.products.forEach((product) =>
          product.productData.forEach((data) => {
            keys.push(...Object.keys(data));
          }),
        );
      } else if (productData) {
        // for single product check
        productData.forEach((data) => {
          keys.push(...Object.keys(data));
        });
      }

      function hasSystemValue(key, system) {
        return key === (`${system}Value` || `${system}ValueRange`);
      }
      const hasMetricKeys = keys.some((key) => hasSystemValue(key, 'metric'));
      const hasImperialKeys = keys.some((key) => hasSystemValue(key, 'imperial'));

      this.displayToggle =
        (hasMetricKeys && hasImperialKeys) ||
        (hasMetricKeys && this.system === 'imperial') ||
        (hasImperialKeys && this.system === 'metric');
    },
    hasRangeDetail(data) {
      const system = this.system;

      // data object has either 'value' or 'metricalValue' & 'imperialValue' keys, the same applies for 'unit'
      return (
        (`${system}ValueRange` in data && data[`${system}ValueRange`]) ||
        ('valueRange' in data && data['valueRange'])
      );
    },
    detailsData(data) {
      return data?.filter((item) => !item.tag) || [];
    },
    createFiltersLabels() {
      this.filters.forEach((filter) => {
        if (filter.type === 'number') {
          filter.options && this.createNumberLabel(filter.options, filter.unit);
          filter.metricOptions && this.createNumberLabel(filter.metricOptions, filter.metricUnit);
          filter.imperialOptions &&
            this.createNumberLabel(filter.imperialOptions, filter.imperialUnit);
        } else if (filter.type === 'range') {
          filter.options && this.createRangeLabel(filter.options, filter.unit);
          filter.metricOptions && this.createRangeLabel(filter.metricOptions, filter.metricUnit);
          filter.imperialOptions &&
            this.createRangeLabel(filter.imperialOptions, filter.imperialUnit);
        }
      });
    },
    createNumberLabel(options, unit) {
      const useUnit = unit || '';
      options.forEach((option) => {
        option.label = `${option.value.toLocaleString(this.locales)} ${useUnit}`;
      });
    },
    createRangeLabel(options, unit) {
      const useUnit = unit || '';
      options.forEach((option) => {
        if (option.valueRange.length === 1) {
          option.label = `${option.valueRange[0].toLocaleString(this.locales)} ${useUnit}`;
        } else if (option.valueRange.length === 2) {
          option.label = `${option.valueRange[0].toLocaleString(
            this.locales,
          )} ${useUnit} - ${option.valueRange[1]?.toLocaleString(this.locales)} ${useUnit}`;
        }
      });
    },
  },
});
